/**
 * Centralised deck-building rules for Sorcery.
 *
 * All deck-building constraints live here. Max copies per rarity,
 * type restrictions, and avatar-specific modifications. Import from
 * this file whenever you need to validate or enforce deck rules
 * anywhere in the app.
 *
 * Avatar-specific rules (Magician spellbook-allows-sites, Pathfinder
 * atlas-no-duplicates, Duplicator pairs-of-uniques, etc.) are defined
 * in `src/utils/game/avatarRules/` and consumed here. Pass the
 * currently selected avatar via the `avatar` option on canAddCard
 * and getMaxCopies; when no avatar is provided, the functions behave
 * exactly as they did before avatar rules existed.
 */

import { resolveAvatarRules } from '../game/avatarRules';
import { isFreePlay } from '../freePlay';
import { isBasicSite } from './basicSites';

/** Maximum copies of a card allowed in a single deck, by rarity. */
export const MAX_COPIES_BY_RARITY = {
  Ordinary: 4,
  Exceptional: 3,
  Elite: 2,
  Unique: 1,
};

/** Default max copies if rarity is unknown or missing. */
export const MAX_COPIES_DEFAULT = 4;

/**
 * Constructed minimums, per the December 2025 rulebook (previously
 * 40/20 in Alpha/Beta, then an interim 50/30). Enforced by canPlayDeck.
 */
export const MIN_SPELLBOOK_SIZE = 60;
export const MIN_ATLAS_SIZE = 30;
export const MAX_COLLECTION_SIZE = 10;

/** Card types that are limited to one per deck. */
export const SINGLETON_TYPES = new Set(['Avatar']);

/**
 * Canonical Sorcery wording for cards that override the rarity copy cap.
 * The pack minions Grey Wolves and Relentless Crowd read "Your spellbook
 * may include any number of <name>." A Constructed-legal permission.
 * Matching on the printed text means any future card with the same
 * deckbuilding permission is covered automatically. No allowlist to keep
 * in sync with new sets.
 */
const UNLIMITED_COPIES_RE = /may include any number/i;

/**
 * True iff the card may be run in unlimited copies in a CONSTRUCTED deck,
 * regardless of its (Ordinary) rarity.
 *
 * Detected from the card's own printed rules text ("may include any
 * number"). Reads the normalized card's `functional_text`
 * (newline-normalized `functional_text_plain` preferred).
 *
 * The basic sites (Spire / Stream / Valley / Wasteland) print "Your atlas
 * may include any number of <name>s." That permission carries no format
 * qualifier, so it applies in Constructed too. Their rules text is not
 * always surfaced to `functional_text`, so match them by id as well.
 */
export function hasUnlimitedCopies(card) {
  if (!card) return false;
  if (isBasicSite(card.unique_id)) return true;
  const text = card.functional_text_plain || card.functional_text || '';
  return UNLIMITED_COPIES_RE.test(text);
}

/**
 * Rarities legal in Peasant format. Avatars and other rarity-less
 * cards pass through (rarity is empty / undefined).
 */
export const PEASANT_LEGAL_RARITIES = new Set(['Ordinary', 'Exceptional']);

/**
 * True iff `card` is legal in a Peasant deck. Cards without a rarity
 * (e.g. Avatars) are considered legal. Peasant only restricts
 * minion / magic / aura / site rarities, not the avatar slot.
 */
export function isPeasantLegal(card) {
  if (!card) return false;
  if (!card.rarity) return true;
  return PEASANT_LEGAL_RARITIES.has(card.rarity);
}

/**
 * Returns the maximum number of copies of a card allowed in a single deck.
 *
 * @param {object} card. Card object with `rarity` and `type` fields
 * @param {object} [options]
 * @param {object} [options.avatar]. The currently-selected avatar card
 *   instance. When present, avatar-specific deckbuilding rules (e.g.
 *   Pathfinder's atlas-no-duplicates) clamp the standard rarity limit.
 * @param {'spellbook'|'atlas'} [options.targetZone]. Which zone the
 *   card is being added to. Only meaningful for cards that could land
 *   in multiple zones (e.g. Site cards under Magician's rules).
 * @returns {number}
 */
export function getMaxCopies(card, { avatar, targetZone } = {}) {
  if (!card) return MAX_COPIES_DEFAULT;
  if (SINGLETON_TYPES.has(card.type)) return 1;

  // Cards that print "may include any number" (Grey Wolves, the basic
  // sites, …) have no rarity copy cap. Avatar-specific clamps below can
  // still narrow this. Pathfinder's atlas-no-duplicates wins for
  // atlas-bound sites via Math.min(Infinity, 1) === 1.
  const baseline = hasUnlimitedCopies(card)
    ? Infinity
    : (MAX_COPIES_BY_RARITY[card.rarity] || MAX_COPIES_DEFAULT);

  if (avatar) {
    const { deckbuilding } = resolveAvatarRules(avatar);

    // Duplicator's "matching pairs of Uniques" rule lifts the Unique
    // rarity cap from 1 → 2 so the player can slot the matching second
    // copy. The whole-deck "exactly two of each" invariant is enforced
    // separately by validateDeckStructure. GetMaxCopies just opens the
    // door wide enough at insert time. Non-Unique rarities are unaffected
    // (Duplicator's requireUniquesOnly already rejects them via canAddCard).
    if (deckbuilding?.requirePairs && baseline === 1) {
      return 2;
    }

    const atlasCap = deckbuilding?.atlasMaxCopies;
    if (typeof atlasCap === 'number' && isAtlasBound(card, targetZone, deckbuilding)) {
      return Math.min(baseline, atlasCap);
    }
  }

  return baseline;
}

function countTowardLimit(card, chosenCards, options = {}) {
  if (SINGLETON_TYPES.has(card.type)) {
    // Singleton (Avatar) is normally a global rule. One per deck across
    // every zone, including the collection. Imposter's deckbuilding rule
    // (allowExtraAvatars) carves out an exception: only the primary
    // avatar slot counts, so additional avatars may live in the
    // spellbook or the collection (sideboard) without tripping the cap.
    const { avatar } = options;
    const rules = avatar ? resolveAvatarRules(avatar).deckbuilding : null;
    if (rules?.allowExtraAvatars) {
      return chosenCards.filter(
        (e) => e.card?.type === card.type && e.zone === 'avatar',
      ).length;
    }
    return chosenCards.filter((e) => e.card?.type === card.type).length;
  }
  // Rarity copy caps are deck-wide. The collection is a searchable
  // in-match pile, so copies parked there are still playable and count.
  return chosenCards.filter((e) => e.card?.unique_id === card.unique_id).length;
}

// A card is "atlas-bound" (and subject to atlas-specific rules like
// Pathfinder's no-duplicates) if it's a Site card heading to the Atlas
// zone. Magician's spellbookAllowsSites redirects sites to Spellbook.
// in that case the atlas cap doesn't apply because the card isn't
// actually in Atlas.
function isAtlasBound(card, targetZone, deckbuilding) {
  if (deckbuilding?.forbidAtlas) return false;
  if (deckbuilding?.spellbookAllowsSites && targetZone === 'spellbook') return false;
  // When targetZone is omitted, fall back to the card's type: Sites
  // normally land in Atlas.
  if (targetZone === 'atlas') return true;
  if (!targetZone) return card.type === 'Site';
  return false;
}

/**
 * Returns how many more copies of `card` can still be added to the
 * current deck without violating its copy limit.
 */
export function getRemainingCopies(card, chosenCards, options = {}) {
  if (!card) return 0;
  return Math.max(0, getMaxCopies(card, options) - countTowardLimit(card, chosenCards || [], options));
}

/**
 * Validates whether a card can be added to the current deck.
 * Returns `{ allowed: true }` or `{ allowed: false, reason: string }`.
 *
 * @param {object} card. The card to add
 * @param {Array} chosenCards. Current deck entries (each has `.card`)
 * @param {object} [options]
 * @param {Map} [options.ownedMap]. CardId → total owned quantity
 * @param {Map} [options.usedElsewhereMap]. CardId → qty used in other decks
 * @param {object} [options.avatar]. The currently-selected avatar
 * @param {'spellbook'|'atlas'} [options.targetZone]. Where the card is headed
 * @param {string} [options.gameMode]. The deck's format tag. When set
 *   to 'peasant', Elite and Unique cards are rejected at add time so
 *   the user can't build an illegal deck and only find out at save.
 */
export function canAddCard(card, chosenCards, options = {}) {
  if (!card) return { allowed: false, reason: 'Invalid card' };

  const { avatar, targetZone, gameMode } = options;
  const rules = avatar ? resolveAvatarRules(avatar).deckbuilding : null;

  // Avatar-specific gates run before copy-limit checks so a clear
  // rejection message ("Magician can't use an atlas") isn't masked by
  // a generic copy-count error when the deck is empty.
  if (rules) {
    const avatarCheck = checkAvatarConstraints(card, targetZone, rules);
    if (!avatarCheck.allowed) return avatarCheck;
  }

  if (gameMode === 'peasant' && !isPeasantLegal(card)) {
    return { allowed: false, reason: `Peasant decks only allow Ordinary and Exceptional cards (${card.name} is ${card.rarity})` };
  }

  const isSingleton = SINGLETON_TYPES.has(card.type);
  // Imposter's `allowExtraAvatars` carve-out: the singleton rule only
  // guards the primary avatar slot. Sending an Avatar to spellbook or
  // collection while Imposter is equipped is a legitimate sideboard
  // pick and must skip the per-deck-copy cap entirely.
  const isExtraAvatarAdd =
    isSingleton
    && rules?.allowExtraAvatars
    && targetZone
    && targetZone !== 'avatar';
  if (!isExtraAvatarAdd) {
    const maxCopies = getMaxCopies(card, { avatar, targetZone });
    const countForLimit = countTowardLimit(card, chosenCards, { avatar });

    if (countForLimit >= maxCopies) {
      if (isSingleton) {
        return { allowed: false, reason: `Only one ${card.type} is allowed per deck` };
      }
      if (maxCopies === 1 && card.type === 'Site' && rules?.atlasMaxCopies === 1) {
        return { allowed: false, reason: `This avatar's atlas can't contain duplicates (${card.name})` };
      }
      return {
        allowed: false,
        reason: `Maximum ${maxCopies} ${maxCopies === 1 ? 'copy' : 'copies'} of ${card.rarity || ''} cards allowed (${card.name})`,
      };
    }
  }

  return { allowed: true };
}

function checkAvatarConstraints(card, targetZone, rules) {
  // "You have no atlas" (Magician): any Site card forced into the
  // Atlas zone is illegal. When the spellbook-allows-sites flag is
  // also set, the UI routes sites to Spellbook so this check is moot,
  // but we still guard against targetZone='atlas' being passed anyway.
  if (rules.forbidAtlas && (targetZone === 'atlas' || (card.type === 'Site' && !rules.spellbookAllowsSites))) {
    return { allowed: false, reason: `This avatar can't use an atlas. Add sites to your spellbook instead` };
  }

  // "Uniques only" (Duplicator): reject any non-Unique-rarity card,
  // except the Avatar itself (Avatars have no rarity).
  if (rules.requireUniquesOnly && card.rarity && card.rarity !== 'Unique' && card.type !== 'Avatar') {
    return { allowed: false, reason: `This avatar only allows Unique cards (${card.name} is ${card.rarity})` };
  }

  return { allowed: true };
}

/**
 * True iff the active avatar's deckbuilding rules categorically ban this
 * card from the playable deck (regardless of count). Examples:
 *   - Duplicator (`requireUniquesOnly`) bans every non-Unique rarity.
 *   - (Future) avatars that ban a type or subtype would extend here.
 *
 * Used by the deck-editor grid to suppress the copy-capacity indicator
 * for banned cards. Showing "0 / N" on a card you literally can't slot
 * misleads the player. Cards that are merely RATE-LIMITED (Pathfinder's
 * atlas-no-duplicates clamps to 1 copy) are NOT banned; only categorical
 * exclusions live here.
 *
 * Avatars themselves are never banned by this check. The Avatar slot
 * has its own singleton + Imposter logic.
 */
export function isCardBannedByAvatar(card, avatar) {
  if (!card || !avatar) return false;
  if (card.type === 'Avatar') return false;
  const rules = resolveAvatarRules(avatar)?.deckbuilding;
  if (!rules) return false;
  if (rules.requireUniquesOnly && card.rarity && card.rarity !== 'Unique') {
    return true;
  }
  return false;
}

/**
 * Returns a human-readable label for the copy limit of a card.
 */
export function getCopyLimitLabel(card, options = {}) {
  if (!card) return '';
  if (SINGLETON_TYPES.has(card.type)) return `1 per deck (${card.type})`;
  const max = getMaxCopies(card, options);
  return `Max ${max} ${max === 1 ? 'copy' : 'copies'} (${card.rarity || 'Standard'})`;
}

/**
 * Resolve which zone a card belongs to, accounting for the current
 * avatar. Site cards normally go to Atlas, but Magician's
 * spellbookAllowsSites rule folds them into Spellbook.
 *
 * @param {object} card. Card object with `_sorceryCategory` or `type`
 * @param {object} [avatar]. The currently-selected avatar
 * @param {object} [options]
 * @param {boolean} [options.primaryAvatarPresent]. When true, the deck
 *   already has an Avatar in the primary slot. Used by avatars with
 *   `allowExtraAvatars` (Imposter) to route additional avatars to the
 *   spellbook by default instead of the avatar slot.
 * @returns {'spellbook'|'atlas'|'avatar'|'collection'}
 */
export function resolveCardZone(card, avatar, options = {}) {
  // Prefer the explicit `_sorceryCategory` set by normalizeCards.js,
  // but fall back to `card.type` so callers that pass partially-built
  // entries (or test stubs) still classify correctly. Without the
  // fallback, an Avatar entry whose card lacks `_sorceryCategory` would
  // hit the final default branch and resolve to 'spellbook'. Wrong.
  const rawCategory = card?._sorceryCategory || card?.type;
  const category = typeof rawCategory === 'string' ? rawCategory.toLowerCase() : '';
  const rules = avatar ? resolveAvatarRules(avatar).deckbuilding : null;

  if (category === 'avatar') {
    // Imposter carve-out: a second avatar lands in the spellbook by
    // default, since the primary slot is already filled. The "C" hotkey
    // and drag-and-drop can still place it in the collection via an
    // explicit targetZone.
    if (options.primaryAvatarPresent && rules?.allowExtraAvatars) return 'spellbook';
    return 'avatar';
  }
  if (category === 'spell') return 'spellbook';

  if (category === 'site') {
    if (rules?.spellbookAllowsSites) return 'spellbook';
    return 'atlas';
  }
  return 'spellbook';
}

/**
 * Re-route every card in a hydrated deck to the zone that its avatar
 * currently dictates. Used at load / import time and after any in-editor
 * avatar change (add or remove) so the deck stays consistent with the
 * currently-active avatar's deckbuilding rules.
 *
 * Three scenarios this handles:
 *   1. Load / import: decks saved before an avatar rule existed (e.g.
 *      Magician's spellbookAllowsSites) get their zones corrected
 *      transparently. The user opens the deck and the cards are
 *      already in the right zones.
 *   2. Swap to Magician (or another atlas-forbidding avatar): existing
 *      atlas sites move to spellbook so they don't get stuck in the
 *      now-hidden Atlas panel.
 *   3. Swap away from Magician: spellbook-routed sites move back to
 *      atlas under the new avatar's standard rules. Also covers the
 *      transient "no avatar at all" state during a swap. Sites still
 *      get re-routed to atlas, the default zone for the Site type.
 *
 * The migration is non-destructive: cards that are already in the
 * correct zone are left untouched, and no card is ever deleted.
 * Sideboard (collection) entries are preserved as-is. They're user-
 * chosen overflow and not subject to avatar zone routing.
 *
 * @param {Array} cards. Hydrated deck entries ({ card, printing, zone })
 * @returns {Array} a new array with zones re-resolved
 */
export function applyAvatarZoneRouting(cards) {
  if (!Array.isArray(cards) || cards.length === 0) return cards || [];
  // The primary avatar is the one occupying the avatar slot. Picking by
  // type alone would wrongly grab an Imposter-allowed extra sitting in
  // the spellbook or collection. Fall back to a type lookup only when no
  // entry is explicitly in the avatar zone yet (e.g. on first hydrate of
  // a legacy save that doesn't carry per-entry zones).
  const avatar =
    cards.find((e) => e.card?.type === 'Avatar' && e.zone === 'avatar')?.card
    || cards.find((e) => e.card?.type === 'Avatar' && !e.zone)?.card
    || null;
  const avatarId = avatar?.unique_id || null;
  return cards.map((entry) => {
    if (entry.zone === 'collection' || entry.isSideboard) return entry;
    // Don't re-route avatar entries. Extras are positioned by the user
    // (default add, S/C hotkeys, drag) and the routing helper has no
    // business overwriting that choice.
    if (entry.card?.type === 'Avatar' && entry.zone && entry.zone !== 'avatar') return entry;
    // Imposter carve-out: an Avatar entry that is NOT the deck's primary
    // avatar must be routed with `primaryAvatarPresent: true` so
    // resolveCardZone sends it to the spellbook (per allowExtraAvatars).
    // Without this flag, every Avatar-typed entry resolves to the
    // 'avatar' slot. And spawnDeck then picks whichever came last,
    // putting the wrong face on the playmat.
    const isExtraAvatar = entry.card?.type === 'Avatar'
      && avatarId
      && entry.card?.unique_id !== avatarId;
    const correctZone = resolveCardZone(entry.card, avatar, {
      primaryAvatarPresent: isExtraAvatar,
    });
    return entry.zone === correctZone ? entry : { ...entry, zone: correctZone };
  });
}

/**
 * Whole-deck structural validation. Catches rules that can only be
 * verified against the completed deck. Duplicator's pairs, Duplicator's
 * uniques-only, anything else that needs a full-deck view.
 *
 * Per-card-add rules live in canAddCard. This function is meant to be
 * called at save or match-entry time with the finished deck.
 *
 * @param {Array} deckCards. Deck entries, each with `.card`
 * @param {object} [avatar]. The currently-selected avatar
 * @param {object} [options]
 * @param {string} [options.gameMode]. The deck's format tag. When set
 *   to 'peasant', Elite/Unique cards are flagged as invalid so the
 *   save path can refuse to persist an illegal deck.
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateDeckStructure(deckCards, avatar, options = {}) {
  if (!Array.isArray(deckCards)) return { valid: true, errors: [] };
  const rules = avatar ? resolveAvatarRules(avatar).deckbuilding : null;
  const errors = [];

  // Pair / uniques-only rules apply to the playable deck only
  // (spellbook + atlas + avatar). Accepting full chosenCards here
  // means we filter the sideboard out internally for these branches.
  const playableNonAvatar = deckCards.filter(
    (e) => e.zone !== 'collection' && e.card?.type !== 'Avatar',
  );

  // Rarity copy caps are deck-wide, collection included. canAddCard
  // gates each add, but decks saved before the collection counted can
  // still carry overflow, so the cap is re-checked here at save time.
  // Zone-specific clamps (Pathfinder's atlas cap) are add-time rules,
  // so the deck-wide count is held to the loosest cap across the zones
  // the card sits in.
  const copies = new Map();
  for (const entry of deckCards) {
    const card = entry.card;
    if (!card?.unique_id || card.type === 'Avatar') continue;
    const prev = copies.get(card.unique_id) || { card, n: 0, max: 0 };
    const max = Math.max(prev.max, getMaxCopies(card, { avatar, targetZone: entry.zone }));
    copies.set(card.unique_id, { card, n: prev.n + 1, max });
  }
  for (const { card, n, max } of copies.values()) {
    if (n > max) {
      errors.push(`Maximum ${max} ${max === 1 ? 'copy' : 'copies'} of ${card.rarity || ''} cards allowed (${card.name} has ${n})`);
    }
  }

  if (rules?.requireUniquesOnly) {
    const offenders = playableNonAvatar.filter((e) => e.card?.rarity && e.card.rarity !== 'Unique');
    if (offenders.length) {
      const sample = [...new Set(offenders.map((o) => o.card.name))].slice(0, 3);
      errors.push(`Only Unique-rarity cards are allowed for this avatar. Offenders: ${sample.join(', ')}${offenders.length > sample.length ? '…' : ''}`);
    }
  }

  if (rules?.requirePairs) {
    const counts = new Map();
    for (const entry of playableNonAvatar) {
      const key = entry.card?.unique_id;
      if (!key) continue;
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    const unpaired = [...counts.entries()].filter(([, n]) => n % 2 !== 0);
    if (unpaired.length) {
      errors.push(`Every card must appear in matching pairs (${unpaired.length} card${unpaired.length === 1 ? '' : 's'} unpaired)`);
    }
  }

  if (options.gameMode === 'peasant') {
    // Peasant is a *deck-wide* rule. Spellbook, atlas, AND
    // collection (sideboard) must all be free of Elite/Unique cards.
    // If we filtered to playableNonAvatar here, an Elite stashed in
    // the sideboard would silently bypass the format. The Avatar
    // slot still passes because avatars are rarity-less.
    const offenders = deckCards.filter((e) =>
      e.card && e.card.type !== 'Avatar' && !isPeasantLegal(e.card),
    );
    if (offenders.length) {
      const sample = [...new Set(offenders.map((o) => o.card.name))].slice(0, 3);
      errors.push(`Peasant decks only allow Ordinary and Exceptional cards. Offenders: ${sample.join(', ')}${offenders.length > sample.length ? '…' : ''}`);
    }
  }

  // Imposter rule: only avatars with `allowExtraAvatars` may carry
  // additional avatar cards outside the primary slot. Without it, any
  // avatar in the spellbook or collection blocks save.
  if (!rules?.allowExtraAvatars) {
    const extras = deckCards.filter(
      (e) => e.card?.type === 'Avatar' && e.zone && e.zone !== 'avatar',
    );
    if (extras.length) {
      const names = [...new Set(extras.map((e) => e.card.name).filter(Boolean))];
      errors.push(
        `Only the Imposter avatar can carry extra avatars in your deck. `
        + `Remove ${names.join(', ')} or equip Imposter as your avatar.`,
      );
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Returns the map of card unique_ids that are present in the deck but
 * violate a structural deck rule. Used by the deck-editor sidebar to
 * render a red "invalid" highlight + a tooltip on the offending rows,
 * and by the editor's mutation pipeline to choose which "heads up"
 * toast to fire when a user action newly invalidates an entry.
 *
 * Map<unique_id, { kind, message }> shape:
 *   • `kind`. Discriminator the caller can branch on. Today:
 *       'extra_avatar'. An Avatar card sitting outside the primary
 *                           slot while the active avatar isn't Imposter.
 *       'unpaired_unique'. A Unique card that appears an odd number of
 *                           times while the active avatar requires pairs
 *                           (Duplicator).
 *   • `message`. Human-readable explanation rendered verbatim in the
 *                 sidebar tooltip.
 *
 * Map preserves `.has(id)` semantics so existing call sites that only
 * test membership stay unchanged.
 *
 * @param {Array} chosenCards. Hydrated deck entries
 * @param {object} [avatar]. The currently-equipped primary avatar
 * @returns {Map<string, { kind: string, message: string }>}
 */
export function getInvalidEntries(chosenCards, avatar) {
  if (!Array.isArray(chosenCards) || chosenCards.length === 0) return new Map();
  const rules = avatar ? resolveAvatarRules(avatar).deckbuilding : null;
  const invalid = new Map();

  // Extra-avatar rule. Only Imposter (`allowExtraAvatars`) may carry
  // additional Avatar cards outside the primary slot. Without it, an
  // Avatar in spellbook / collection is a structural error.
  if (!rules?.allowExtraAvatars) {
    for (const e of chosenCards) {
      if (e.card?.type === 'Avatar' && e.zone && e.zone !== 'avatar' && e.card.unique_id) {
        invalid.set(e.card.unique_id, {
          kind: 'extra_avatar',
          message: 'Only the Imposter avatar can carry extra avatars in your deck.',
        });
      }
    }
  }

  // Pair rule. When the avatar requires matching pairs (Duplicator),
  // any playable-deck card with an odd count is unpaired. Flag every
  // copy of the offender so the sidebar tints both rows on a mismatched
  // foiling and the single-copy case stays visible.
  if (rules?.requirePairs) {
    const playable = chosenCards.filter(
      (e) => e.zone !== 'collection' && e.card?.type !== 'Avatar',
    );
    const counts = new Map();
    for (const e of playable) {
      const id = e.card?.unique_id;
      if (!id) continue;
      counts.set(id, (counts.get(id) || 0) + 1);
    }
    for (const [id, n] of counts) {
      if (n % 2 === 0) continue;
      // Don't overwrite a stricter flag. Extra_avatar already wins.
      if (invalid.has(id)) continue;
      invalid.set(id, {
        kind: 'unpaired_unique',
        message: n === 1
          ? 'Needs a matching pair. Add a second copy or remove this card.'
          : `Needs matching pairs. Currently ${n} copies (odd count).`,
      });
    }
  }

  return invalid;
}

/**
 * True iff the deck contains at least one card the player doesn't
 * own in sufficient quantity. Used by the deck list UI to render the
 * "Unowned cards" badge, by the ranked/event deck picker to disable
 * such decks, and by the Sandbox picker (where the answer doesn't
 * matter because Sandbox accepts any deck).
 */
export function isDeckSandboxOnly(deckCards, ownedMap) {
  if (!Array.isArray(deckCards) || deckCards.length === 0) return false;
  if (!ownedMap) return false;
  return getMissingCards(deckCards, ownedMap).count > 0;
}

/**
 * The mode a saved deck plays as. Free play has no Sandbox: a deck saved
 * as 'sandbox' before the switch plays (and re-saves) as constructed.
 */
export function effectiveDeckMode(gameMode) {
  const mode = gameMode || 'constructed';
  return mode === 'sandbox' && isFreePlay() ? 'constructed' : mode;
}

/**
 * Returns the list of cards in a deck that the player doesn't own
 * (or doesn't own enough copies of).
 */
export function getMissingCards(deckCards, ownedMap) {
  // Free play: every card is everyone's, so nothing is ever missing.
  if (!deckCards || !ownedMap || isFreePlay()) return { missing: [], count: 0 };

  const needed = new Map();
  for (const entry of deckCards) {
    const id = entry.card?.unique_id || entry.cardId;
    const name = entry.card?.name || entry.cardName || id;
    if (!id) continue;
    const prev = needed.get(id) || { name, need: 0 };
    prev.need++;
    needed.set(id, prev);
  }

  const missing = [];
  let count = 0;
  for (const [id, { name, need }] of needed) {
    const have = ownedMap.get(id) || 0;
    if (have < need) {
      const deficit = need - have;
      missing.push({ name, need, have });
      count += deficit;
    }
  }

  return { missing, count };
}

/**
 * Check if a deck can be used for matchmaking. Returns a structured
 * result describing whether the deck is playable and, if not, the
 * specific reason. So callers can surface a precise toast instead
 * of a generic "invalid deck" message.
 *
 * Gates in order (first failure short-circuits):
 *   1. Ownership. Every card must be in the player's collection.
 *   2. Structural rules. When a `cardCatalog` is supplied, the deck
 *      is hydrated and validated against its avatar's rules (e.g.
 *      Duplicator pairs, Magician atlas ban).
 *   3. Minimum deck size. 60 spellbook / 30 atlas, the Constructed
 *      minimum since the December 2025 rulebook.
 *
 * The size gate was previously absent because the built-in starter decks
 * shipped as verbatim 36/16 precons and would have been locked out by it.
 * They are now upgraded to 60/30 (see `src/utils/arena/starterDecks.js`),
 * so every deck a player can obtain without deckbuilding is already legal.
 * Sandbox skips the gate along with ownership: its whole purpose is testing
 * partial decks.
 *
 * Saved decks use a minimal format (`{ cardId, printingId, … }` with
 * no card objects), so structural validation needs a catalog lookup
 * to hydrate entries. When omitted, only ownership is validated.
 * matches the pre-avatar-rules behaviour for older call sites.
 *
 * @param {Array} deckCards
 * @param {Map} ownedMap  cardId → owned qty
 * @param {object} [options]
 * @param {Map} [options.cardCatalog] cardId → full card object
 * @returns {{ canPlay: boolean, reason?: string }}
 */
export function canPlayDeck(deckCards, ownedMap, options = {}) {
  // Sandbox mode skips ownership. The whole point is to let players
  // test decks containing cards they don't own yet. Structural rules
  // still apply below.
  const skipOwnership = options.skipOwnership === true || isFreePlay();
  if (!skipOwnership) {
    const { missing, count } = getMissingCards(deckCards, ownedMap);
    if (count > 0) {
      const sampleNames = missing.slice(0, 2).map((m) => m.name).filter(Boolean);
      const more = missing.length > sampleNames.length ? ` and ${missing.length - sampleNames.length} more` : '';
      const reason = sampleNames.length
        ? `You don't own enough copies of ${sampleNames.join(', ')}${more}. Acquire them via packs, the store, or the auction house.`
        : `This deck has cards you don't own yet.`;
      return { canPlay: false, reason };
    }
  }

  const { cardCatalog, gameMode } = options;
  if (cardCatalog) {
    const hydrated = hydrateDeckEntries(deckCards, cardCatalog);
    // Pick the avatar in the primary slot specifically. Imposter decks
    // can carry additional avatar cards in the spellbook/collection,
    // and picking by type alone could grab one of those and apply its
    // (wrong) rules to the whole deck.
    const avatar =
      hydrated.find((e) => e.card?.type === 'Avatar' && e.zone === 'avatar')?.card
      || hydrated.find((e) => e.card?.type === 'Avatar')?.card;
    const structure = validateDeckStructure(hydrated, avatar, { gameMode });
    if (!structure.valid) {
      return { canPlay: false, reason: structure.errors[0] };
    }

    // Deck size is a game rule, not an ownership rule: every mode, solo and
    // sandbox included, enforces 60/30 and the 10-card collection cap.
    // Ownership is the only thing sandbox waives. The server gates the same.
    const size = countDeckZones(hydrated);
    if (size.spellbook < MIN_SPELLBOOK_SIZE) {
      return { canPlay: false, reason: `Your spellbook needs at least ${MIN_SPELLBOOK_SIZE} cards (currently ${size.spellbook})` };
    }
    // Magician has no atlas at all, so an atlas minimum would lock the
    // avatar out entirely. Its sites live in the spellbook and are
    // already counted above.
    const forbidsAtlas = avatar ? resolveAvatarRules(avatar).deckbuilding?.forbidAtlas : false;
    if (!forbidsAtlas && size.atlas < MIN_ATLAS_SIZE) {
      return { canPlay: false, reason: `Your atlas needs at least ${MIN_ATLAS_SIZE} sites (currently ${size.atlas})` };
    }
    if (size.collection > MAX_COLLECTION_SIZE) {
      return { canPlay: false, reason: `Your collection can hold at most ${MAX_COLLECTION_SIZE} cards (currently ${size.collection})` };
    }
  }

  return { canPlay: true };
}

/**
 * Count a hydrated deck's spellbook and atlas cards.
 *
 * Persisted saved decks carry `isSideboard`, not `zone` (see the
 * saveSavedDeck payload in app.jsx), so the fallback chain has to match
 * the one in deckExport.js: explicit zone, else sideboard, else the
 * spellbook-unless-Site convention. Avatars count toward neither
 * minimum. The rulebook excludes them from both deck sizes.
 */
function countDeckZones(hydrated) {
  const size = { spellbook: 0, atlas: 0, collection: 0 };
  for (const entry of hydrated) {
    const zone = entry.zone
      || (entry.isSideboard ? 'collection' : (entry.card?.type === 'Site' ? 'atlas' : 'spellbook'));
    // The primary avatar sits on the board; a sideboard avatar (Imposter's
    // Mask) is a collection card like any other.
    if (entry.card?.type === 'Avatar' && zone !== 'collection') continue;
    if (zone in size) size[zone] += 1;
  }
  return size;
}

// Map an array of minimal saved-deck entries (cardId + printingId)
// into the { card, printing, zone } shape validateDeckStructure
// expects. Entries that already carry a `.card` pass through.
function hydrateDeckEntries(deckCards, cardCatalog) {
  return (deckCards || []).map((entry) => {
    if (entry.card) return entry;
    const id = entry.cardId || entry.card?.unique_id;
    const card = id ? cardCatalog.get?.(id) : null;
    return { ...entry, card };
  });
}

/**
 * Build a cardId→card lookup from the flat sorceryCards array. Cheap.
 * one pass, no allocations per entry. Callers that batch-validate
 * multiple decks (e.g. deck-library filters) should build this once
 * and reuse it.
 */
export function buildCardCatalog(sorceryCards) {
  const map = new Map();
  for (const card of sorceryCards || []) {
    if (card?.unique_id) map.set(card.unique_id, card);
  }
  return map;
}
